import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, MoreHorizontal, Trash2, X, MessageSquare, CheckSquare, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { boardsApi, listsApi, cardsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { BoardWithLists, BoardCard, CreateBoardListRequest } from '@/types'
import { format } from 'date-fns'
import TaskModal from './TaskModal'

interface KanbanViewProps {
  board: BoardWithLists
  boardId: string
  projectName?: string
  onCardClick: (card: BoardCard) => void
}

export default function KanbanView({ board, boardId, projectName, onCardClick }: KanbanViewProps) {
  const queryClient = useQueryClient()
  const [addingListId, setAddingListId] = useState<string | null>(null)
  const [newListName, setNewListName] = useState('')
  const [addingCardToList, setAddingCardToList] = useState<string | null>(null)
  const [draggedCard, setDraggedCard] = useState<{ card: BoardCard; sourceListId: string } | null>(null)
  const [dragOverListId, setDragOverListId] = useState<string | null>(null)

  const createListMutation = useMutation({
    mutationFn: (data: CreateBoardListRequest) => boardsApi.createList(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      setAddingListId(null)
      setNewListName('')
      toast.success('List created')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create list'
      toast.error(errorMessage)
    },
  })

  const deleteListMutation = useMutation({
    mutationFn: (listId: string) => listsApi.delete(listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
      toast.success('List deleted')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete list'
      toast.error(errorMessage)
    },
  })

  const moveCardMutation = useMutation({
    mutationFn: ({ cardId, targetListId, position }: { cardId: string; targetListId: string; position: number }) =>
      cardsApi.move(cardId, { target_list_id: targetListId, position }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to move card'
      toast.error(errorMessage)
    },
  })

  const handleCreateList = () => {
    if (!newListName.trim()) return
    createListMutation.mutate({ list_name: newListName })
  }

  const handleDragStart = (e: React.DragEvent, card: BoardCard, sourceListId: string) => {
    setDraggedCard({ card, sourceListId })
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', card.card_id)
  }

  const handleDragOver = (e: React.DragEvent, listId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverListId(listId)
  }

  const handleDragLeave = () => {
    setDragOverListId(null)
  }

  const handleDrop = (e: React.DragEvent, targetListId: string, targetPosition?: number) => {
    e.preventDefault()
    setDragOverListId(null)

    if (!draggedCard) return

    const { card, sourceListId } = draggedCard

    if (sourceListId === targetListId && targetPosition === card.position) {
      setDraggedCard(null)
      return
    }

    const targetList = board.lists.find(l => l.list_id === targetListId)
    const position = targetPosition ?? (targetList?.cards.length ?? 0)

    moveCardMutation.mutate({
      cardId: card.card_id,
      targetListId,
      position,
    })

    setDraggedCard(null)
  }

  const handleDragEnd = () => {
    setDraggedCard(null)
    setDragOverListId(null)
  }

  return (
    <div className="h-full w-full overflow-x-auto overflow-y-hidden">
      <div className="flex gap-3 p-4 h-full min-w-max">
        {board.lists.map((list) => (
          <div
            key={list.list_id}
            className={`flex-shrink-0 w-72 flex flex-col max-h-full rounded-xl transition-colors ${
              dragOverListId === list.list_id ? 'bg-zinc-100 dark:bg-zinc-900' : 'bg-white dark:bg-black'
            }`}
            onDragOver={(e) => handleDragOver(e, list.list_id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, list.list_id)}
          >
            <div className="flex items-center justify-between p-3 font-semibold text-foreground">
              <span className="flex items-center gap-2">
                {list.list_name}
                <Badge variant="secondary" className="text-xs bg-black/10 dark:bg-white/20 text-foreground border-0">
                  {list.cards?.length || 0}
                </Badge>
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/10">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => deleteListMutation.mutate(list.list_id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete List
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2">
              {list.cards?.map((card, index) => (
                <div
                  key={card.card_id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, card, list.list_id)}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => {
                    e.stopPropagation()
                    handleDrop(e, list.list_id, index)
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  className={`cursor-grab active:cursor-grabbing ${
                    draggedCard?.card.card_id === card.card_id ? 'opacity-50' : ''
                  }`}
                >
                  <Card
                    className="hover:shadow-lg transition-all cursor-pointer bg-zinc-100 dark:bg-zinc-800 border-0 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                    onClick={() => onCardClick(card)}
                  >
                    {card.cover_color && (
                      <div className="h-8 rounded-t-lg" style={{ backgroundColor: card.cover_color }} />
                    )}
                    <CardContent className="p-3 space-y-2">
                      {card.labels && card.labels.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {card.labels.map((label) => (
                            <div
                              key={label.label_id}
                              className="h-2 w-10 rounded-full"
                              style={{ backgroundColor: label.label_color }}
                              title={label.label_name || ''}
                            />
                          ))}
                        </div>
                      )}
                      <p className="text-sm font-medium text-foreground">{card.card_title}</p>
                      {/* Due date row */}
                      {card.due_date && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span className={`flex items-center gap-1 ${
                            new Date(card.due_date) < new Date() && !card.is_completed ? 'text-red-500 dark:text-red-400' : ''
                          }`}>
                            <Clock className="h-3 w-3" />
                            {format(new Date(card.due_date), 'MMM d')}
                          </span>
                        </div>
                      )}
                      {/* Bottom row: metadata on left, assignees on right */}
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {card.comment_count > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {card.comment_count}
                            </span>
                          )}
                          {card.checklists && card.checklists.length > 0 && (
                            <span className="flex items-center gap-1">
                              <CheckSquare className="h-3 w-3" />
                              {card.checklists.reduce((acc, cl) => acc + (cl.items?.filter(i => i.is_completed).length || 0), 0)}/
                              {card.checklists.reduce((acc, cl) => acc + (cl.items?.length || 0), 0)}
                            </span>
                          )}
                        </div>
                        {card.members && card.members.length > 0 && (
                          <div className="flex -space-x-2">
                            {card.members.slice(0, 3).map((member) => (
                              <div
                                key={member.user_id}
                                className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium border-2 border-zinc-100 dark:border-zinc-800"
                                title={member.full_name}
                              >
                                {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                              </div>
                            ))}
                            {card.members.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-zinc-400 dark:bg-zinc-600 text-zinc-700 dark:text-zinc-300 flex items-center justify-center text-xs font-medium border-2 border-zinc-100 dark:border-zinc-800">
                                +{card.members.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}

              <Button
                variant="ghost"
                className="w-full justify-start text-foreground/60 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/10"
                onClick={() => setAddingCardToList(list.list_id)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add a card
              </Button>
            </div>
          </div>
        ))}

        <div className="flex-shrink-0 w-72">
          {addingListId === 'new' ? (
            <div className="bg-white dark:bg-black rounded-xl p-3 space-y-2">
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="Enter list name..."
                autoFocus
                className="bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-foreground placeholder:text-muted-foreground"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateList()
                  if (e.key === 'Escape') {
                    setAddingListId(null)
                    setNewListName('')
                  }
                }}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleCreateList} className="bg-blue-500 hover:bg-blue-600 text-white">
                  Add List
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-foreground/70 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/10"
                  onClick={() => {
                    setAddingListId(null)
                    setNewListName('')
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              className="w-full justify-start bg-black/20 dark:bg-white/20 hover:bg-black/30 dark:hover:bg-white/30 text-white"
              onClick={() => setAddingListId('new')}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add another list
            </Button>
          )}
        </div>
      </div>

      {/* Task Modal for creating new cards */}
      {addingCardToList && (
        <TaskModal
          listId={addingCardToList}
          lists={board.lists}
          boardId={boardId}
          projectName={projectName}
          labels={board.labels || []}
          onClose={() => setAddingCardToList(null)}
          mode="create"
        />
      )}
    </div>
  )
}

